<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    content-class="bg-white b-1 bc-outlined-gray"
    offset="5"
    eager
  >
    <template #activator="{ props: menuProps }">
      <button
        v-if="!inlineStyle"
        @keyup.down="focusOnLabeledVolume(1, 0)"
        v-bind="menuProps"
        ref="childrenMenuActivator"
        :ripple="false"
        class="bg-white c-black fullheight fullwidth rounded pa-3 focus-very-visible"
      >
        <v-row>
          <v-col
            class="ta-left"
            cols="10"
          >
            <div
              class="fs-16 mb-1"
              data-cy="children_dropdown"
              data-testid="children_dropdown"
            >
              {{ $bct('Children') }}
            </div>
            <div
              :aria-label="$bct(addChildrenText)"
              class="fs-16 fw-600"
            >
              {{ $bct(addChildrenText) }}
            </div>
          </v-col>
          <v-col
            class="d-flex align-center justify-end"
            cols="2"
          >
            <v-icon icon="expand_more" />
          </v-col>
        </v-row>
      </button>

      <v-btn
        v-else
        @keydown.tab="focusOnLabeledVolume(1, 0)"
        v-bind="props"
        ref="childrenMenuActivator"
        :aria-label="$bct('Change age groups')"
        append-icon="expand_more"
        class="justify-space-between"
        color="surface-light"
        variant="flat"
        block
      >
        <v-icon
          aria-hidden="true"
          class="me-2 fs-24"
          color="primary"
          icon="person_outline"
          role="presentation"
        />
        <span
          :aria-label="$bct(addChildrenText)"
          class="mxw-70pc constrained fs-16"
        >
          {{ $bct(addChildrenText) }}
        </span>
        <v-spacer />
      </v-btn>
    </template>

    <SearchDobChild
      @on-add-child="(child) => emit('onAddChild', child)"
      @on-remove-child="(index) => emit('onRemoveChild', index)"
      @on-update-child="(child, index) => emit('onUpdateChild', child, index)"
      ref="searchDobChild"
      :children="children"
    />

    <v-card-actions>
      <v-spacer />

      <v-btn
        @click="close"
        :ripple="false"
        class="focus-very-visible mt-3"
        color="primary"
        data-cy="children_done"
        data-testid="children_done"
        block
      >
        {{ $bct('Done') }}
      </v-btn>
    </v-card-actions>
  </v-menu>
</template>

<script setup>
import SearchDobChild from '@/parent/components/searches/SearchDobChild.vue';

const props = defineProps({
  children: {
    type: Array,
    default: () => [{ dob: null, care_needed_date: null }],
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  inlineStyle: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['onAddChild', 'onRemoveChild', 'onUpdateChild']);

const $bct = inject('translator');

const menu = ref(false);
const childrenMenuActivator = ref(null);
const searchDobChild = ref(null);

const addChildrenText = computed(() => {
  const childQuerySum = props.children.filter(
    (child) => child.dob !== null && child.care_needed_date !== null,
  ).length;

  const label = `${childQuerySum} ${childQuerySum === 1 ? 'child' : 'children'}`;

  if (!props.inlineStyle) {
    if (childQuerySum > 0) {
      return label;
    }
    return 'Add children';
  }

  return label;
});

onMounted(() => {
  if (props.autofocus) focus();
});

function close() {
  const closeable = searchDobChild.value.handleDoneClick();

  if (closeable) {
    menu.value = false;
  }
}

function focus() {
  childrenMenuActivator.value.focus();
}

function focusOnLabeledVolume(x, y) {
  searchDobChild.value.$refs.childVolume[y].focus();
}
</script>

<style>
.centered {
  margin-left: auto;
  margin-right: auto;
}

.card-break {
  border-top: 1px #eeeceb solid;
}

.bg-white {
  background-color: white;
}

#children_menu .v-input {
  padding-top: 12px;
  margin-top: 4px;
}
</style>
